import { useQuery } from '@tanstack/react-query'
import { missionService } from '@/services/missionService'
import { useApiReady } from '@/hooks/useApiReady'

// Query keys
export const missionsListKeys = {
  all: ['missions'] as const,
  lists: () => [...missionsListKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...missionsListKeys.lists(), filters] as const,
}

export interface MissionsListFilters {
  page?: number
  pageSize?: number
  search?: string
  status?: string[]
  freelancerId?: string
  sortBy?: string
  sortDirection?: 'asc' | 'desc'
  teamId?: string
  createdAtFrom?: string
  createdAtTo?: string
  projectCodeId?: string
  hasProjectCode?: 'with' | 'without'
  hasPurchaseOrder?: 'with' | 'without'
  missionType?: string
  jobTitle?: string
  teamTagIds?: string[]
  teamUnitIds?: string[]
  kycComplete?: 'complete' | 'incomplete'
  masterAgreement?: 'active' | 'inactive'
  umbrella?: 'yes' | 'no'
  isFavorite?: boolean
  startingSoonAlert?: 'with' | 'without'
}

function missionsListQueryKey(filters: MissionsListFilters) {
  return missionsListKeys.list({
    page: filters.page,
    pageSize: filters.pageSize,
    search: filters.search,
    status: filters.status,
    freelancerId: filters.freelancerId,
    sortBy: filters.sortBy,
    sortDirection: filters.sortDirection,
    teamId: filters.teamId,
    createdAtFrom: filters.createdAtFrom,
    createdAtTo: filters.createdAtTo,
    projectCodeId: filters.projectCodeId,
    hasProjectCode: filters.hasProjectCode,
    hasPurchaseOrder: filters.hasPurchaseOrder,
    missionType: filters.missionType,
    jobTitle: filters.jobTitle,
    teamTagIds: filters.teamTagIds,
    teamUnitIds: filters.teamUnitIds,
    kycComplete: filters.kycComplete,
    masterAgreement: filters.masterAgreement,
    umbrella: filters.umbrella,
    isFavorite: filters.isFavorite,
    startingSoonAlert: filters.startingSoonAlert,
  })
}

function buildMissionsListQueryFn(filters: MissionsListFilters) {
  return async () => {
    let statusForApi: string[] | undefined = undefined
    if (Array.isArray(filters.status) && filters.status.length > 0) {
      const statusMap: Record<string, string> = {
        'pending-approval': 'pending-approval',
        'awaiting-freelancer-acceptance': 'awaiting-freelancer-acceptance',
        'awaiting-company-signature': 'awaiting-team-signature',
        'in-progress': 'in-progress',
        completed: 'completed',
        terminated: 'terminated',
        deleted: 'deleted',
        draft: 'draft',
        rejected: 'rejected',
        cancelled: 'cancelled',
      }

      const rawStatuses = filters.status
        .map((s) => {
          const normalized = s.toLowerCase().replace(/_/g, '-')
          return statusMap[normalized] || normalized
        })
        .filter(Boolean)
        .map((s) => s.toLowerCase().replace(/_/g, '-'))

      if (rawStatuses.length > 0) {
        statusForApi = rawStatuses
      }
    }

    const result = await missionService.getMissions({
      page: filters.page,
      pageSize: filters.pageSize || 10,
      search: filters.search,
      status: statusForApi,
      freelancerId: filters.freelancerId,
      teamId: filters.teamId,
      sortBy: filters.sortBy,
      sortDirection: filters.sortDirection,
      createdAtFrom: filters.createdAtFrom,
      createdAtTo: filters.createdAtTo,
      projectCodeId: filters.projectCodeId,
      hasProjectCode: filters.hasProjectCode,
      hasPurchaseOrder: filters.hasPurchaseOrder,
      missionType: filters.missionType,
      jobTitle: filters.jobTitle || undefined,
      teamTagIds: filters.teamTagIds,
      teamUnitIds: filters.teamUnitIds,
      kycComplete: filters.kycComplete || undefined,
      masterAgreement: filters.masterAgreement || undefined,
      umbrella: filters.umbrella || undefined,
      isFavorite: filters.isFavorite || undefined,
      startingSoonAlert: filters.startingSoonAlert,
    })

    if (result.success) {
      return {
        list: result.data.list || [],
        total: result.data.total || 0,
      }
    }

    return { list: [], total: 0 }
  }
}

/** Options partagées pour prefetch ou useQuery externe. */
export function getMissionsListQueryOptions(filters: MissionsListFilters) {
  return {
    queryKey: missionsListQueryKey(filters),
    queryFn: buildMissionsListQueryFn(filters),
  }
}

/**
 * Hook pour récupérer la liste des missions
 */
export function useMissionsList(filters: MissionsListFilters, options?: { enabled?: boolean }) {
  const apiReady = useApiReady()
  return useQuery({
    ...getMissionsListQueryOptions(filters),
    enabled: apiReady && options?.enabled !== false,
  })
}
